import type { DsIconName } from '~/assets/ds/icons';
import { type NavVisibility, pruneHidden } from '~/utils/navVisibility';

// Configuration de navigation pour la sidebar v2 (chrome Design System).
// Miroir de la config du composant legacy (Layout/Default/SideBarComponent) : mêmes
// liens (externes ARC + routes internes) et mêmes gates de permission, restructurés
// façon kit (rail d'icônes + flyout groupé pour Admin). Extraction partagée avec le legacy
// à consolider quand la v2 deviendra le défaut.

// `visible` est porté par CHAQUE enfant : seule source de vérité des permissions de navigation.
export interface NavChild extends NavVisibility { label: string; page: string; visible: boolean }
// Ceux d'un groupe (`visible`, `opensParent`) sont dérivés de ses liens, jamais déclarés.
export interface NavGroup extends NavVisibility { title: string; items: NavChild[] }
export interface NavItem {
  label: string;
  // Typé sur le set DS : l'item traverse un `:name="it.icon"`, hors de portée du compilateur.
  icon: DsIconName;
  page?: string;
  external?: boolean;
  disabled?: boolean;
  // Entrées sans groupes uniquement : `withGroupVisibility` l'écrase pour les autres.
  visible?: boolean;
  groups?: NavGroup[];
}

// Une entrée qui mène quelque part est un vrai lien, pour que Ctrl+clic et « ouvrir dans un nouvel
// onglet » restent au navigateur. Partagé par le rail desktop et le drawer mobile.
export function navTag (item: NavItem): 'a' | 'button' {
  return (item.groups || item.disabled || !item.page) ? 'button' : 'a';
}

// La règle de `pruneHidden` s'applique aux deux niveaux : un groupe vidé de ses liens partirait
// sinon en titre orphelin, et `opensParent` remonte des liens à l'entrée. Filtrage fait ici une
// fois pour toutes — les consommateurs lisent `it.groups` tel quel.
function withGroupVisibility (items: NavItem[]): NavItem[] {
  return items.map((it) => {
    if (!it.groups) { return it; }
    const groups = it.groups.map((g) => {
      const { kept, visible, opensParent } = pruneHidden(g.items);
      return { ...g, items: kept, visible, opensParent };
    });
    const { kept, opensParent } = pruneHidden(groups);
    return { ...it, groups: kept, visible: opensParent };
  });
}

export function useSidebarNav (): { mainItems: ComputedRef<NavItem[]>; footerItems: ComputedRef<NavItem[]> } {
  const config = useRuntimeConfig();
  const { userHasPermission } = useUsersStore();
  const arc = config.public.ARC_URL ?? '';

  const mainItems = computed<NavItem[]>(() => withGroupVisibility([
    { label: 'Accueil', icon: 'home', page: `${arc}/liste-taches`, external: true, visible: true },
    { label: 'Tâches', icon: 'list', page: `${arc}/liste-taches`, external: true, visible: true },
    { label: 'Dossiers', icon: 'folder', page: `${arc}/recherche_dossiers.php`, external: true, visible: true },
    { label: 'Agenda', icon: 'calendar', page: '/agenda', disabled: true, visible: true },
    {
      label: 'Commerce', icon: 'briefcase',
      groups: [{ title: 'Commerce', items: [
        { label: 'Site', page: '/commerce/sites', visible: userHasPermission('CAN_ACCESS_ADMIN_SITE') },
        { label: 'Contact', page: '/commerce/contacts', visible: userHasPermission('CAN_ACCESS_ADMIN_CONTACT') },
      ] }],
    },
    { label: 'Reporting', icon: 'chart-bar', page: `${arc}/mains_reports.php`, external: true, visible: true },
  ]));

  const footerItems = computed<NavItem[]>(() => withGroupVisibility([
    {
      label: 'Admin', icon: 'cog',
      groups: [
        { title: 'Référentiels financiers', items: [
          { label: 'Types de revenu', page: '/admin/income_types', visible: userHasPermission('CAN_ACCESS_ADMIN_INCOME_TYPE') },
          { label: 'Types de crédit', page: '/admin/credit_types', visible: userHasPermission('CAN_ACCESS_ADMIN_CREDIT_TYPE') },
          { label: 'Types de charge', page: '/admin/charge_types', visible: userHasPermission('CAN_ACCESS_ADMIN_CHARGE_TYPE') },
          { label: 'Types de transaction', page: '/admin/transaction_types', visible: userHasPermission('CAN_ACCESS_ADMIN_BANK_ACCOUNT_TRANSACTION_TYPE') },
          { label: 'Types d\'assurances', page: '/admin/insurance_types', visible: userHasPermission('CAN_ACCESS_ADMIN_INSURANCE_TYPE') },
        ] },
        { title: 'Produits & partenaires bancaires', items: [
          { label: 'Produits bancaires', page: '/admin/bank_products', visible: userHasPermission('CAN_ACCESS_ADMIN_BANK_PRODUCT') },
          { label: 'Commissions produits bancaires', page: '/admin/bank_product_commissions', visible: userHasPermission('CAN_ACCESS_ADMIN_COMMISSION') },
          { label: 'Banques', page: '/admin/banks', visible: userHasPermission('CAN_ACCESS_ADMIN_BANK') },
          { label: 'Marques', page: '/admin/brands', visible: userHasPermission('CAN_ACCESS_ADMIN_BRAND') },
          { label: 'Taux d\'usure', page: '/admin/usury_rates', visible: userHasPermission('CAN_ACCESS_ADMIN_USURY_RATE'), opensParent: false },
        ] },
        { title: 'Paramètres métier', items: [
          { label: 'Orientation', page: '/admin/orientation', visible: userHasPermission('CAN_ACCESS_ADMIN_ORIENTATION_RULE') },
          { label: 'Positions', page: '/admin/positions', visible: userHasPermission('CAN_ACCESS_ADMIN_POSITION') },
        ] },
        { title: 'CRM & contacts', items: [
          { label: 'Contact positions', page: '/admin/contact_positions', visible: userHasPermission('CAN_ACCESS_ADMIN_CONTACT_POSITION'), opensParent: false },
          { label: 'Types d\'événements contact', page: '/admin/contact_event_types', visible: userHasPermission('CAN_ACCESS_ADMIN_CONTACT_EVENT_CONTACT_EVENT_TYPE') },
        ] },
        { title: 'Documents', items: [
          { label: 'Documents exclus', page: '/admin/excluded_documents', visible: userHasPermission('CAN_ACCESS_ADMIN_DOCUMENT_EXCLUDED') },
        ] },
      ],
    },
  ]));

  return { mainItems, footerItems };
}
